/**
 * AbilityPresets — VFX preset configurations for impact, cast, trail, and aura effects.
 *
 * Extracted from AbilityBuilder to keep preset data definitions separate
 * from the fluent API logic.
 */

import { VFXEffectRegistry, VFXPrimitive } from '../../../effects/VFXEffectRegistry';
import { projectileRibbon } from '../../../effects/config/abilities/trailBuilders';

// =============================================================================
// IMPACT VFX PRESETS
// =============================================================================

export const IMPACT_VFX_CONFIGS: Record<string, VFXPrimitive[]> = {
  explosion: [
    { type: 'circle', radius: 30, color: 0xFF4500, alpha: 0.6, depth: 94, tween: { alpha: 0, scale: 2.5, duration: 400, ease: 'Power2' } },
    { type: 'circle', radius: 8, color: 0xFFDD44, alpha: 0.8, depth: 96, tween: { alpha: 0, scale: 0.3, duration: 300 }, scatter: { count: 8, radius: 40 } },
  ],
  spark: [
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 6, color: 0xFFFFFF, alpha: 0.9, depth: 96, tween: { alpha: 0, rotation: Math.PI * 0.5, duration: 200 }, scatter: { count: 6, radius: 25 } },
  ],
  shatter: [
    { type: 'rect', width: 6, height: 6, color: 0xADD8E6, alpha: 0.8, depth: 95, tween: { y: -20, alpha: 0, rotation: Math.PI, duration: 400 }, scatter: { count: 8, radius: 30 } },
  ],
  freeze: [
    { type: 'circle', radius: 25, color: 0x00FFFF, alpha: 0.4, depth: 93, tween: { alpha: 0, scale: 2, duration: 500, ease: 'Power2' } },
    { type: 'star', points: 6, innerRadius: 3, outerRadius: 7, color: 0xFFFFFF, alpha: 0.8, depth: 95, tween: { alpha: 0, rotation: Math.PI * 0.3, duration: 400 }, scatter: { count: 5, radius: 20 } },
  ],
  burn: [
    { type: 'circle', radius: 6, color: 0xFF4500, alpha: 0.8, depth: 94, tween: { y: -20, alpha: 0, scale: 0.4, duration: 400 }, scatter: { count: 6, radius: 20 } },
    { type: 'circle', radius: 15, color: 0xFF0000, alpha: 0.3, depth: 92, tween: { alpha: 0, scale: 1.5, duration: 350 } },
  ],
};

// =============================================================================
// CAST VFX PRESETS
// =============================================================================

export const CAST_VFX_CONFIGS: Record<string, VFXPrimitive[]> = {
  charge: [
    { type: 'circle', radius: 20, color: 0xFFFFFF, alpha: 0.3, depth: 90, tween: { alpha: 0.8, scale: 0.5, duration: 300, yoyo: true, repeat: 2 } },
  ],
  glow: [
    { type: 'circle', radius: 30, color: 0xFFDD44, alpha: 0.2, depth: 89, tween: { alpha: 0.5, scale: 1.3, duration: 500, yoyo: true, repeat: 1 } },
  ],
  summon: [
    { type: 'circle', radius: 5, color: 0xFFFFFF, alpha: 0.8, depth: 94, tween: { alpha: 0, y: -30, duration: 600 }, scatter: { count: 8, radius: 40 } },
  ],
  channel: [
    { type: 'ellipse', width: 40, height: 40, color: 0x4488FF, alpha: 0.2, depth: 89, stroke: { color: 0x4488FF, width: 2 }, tween: { scale: 1.1, alpha: 0.4, duration: 400, yoyo: true, repeat: -1 } },
  ],
};

// =============================================================================
// PRESET REGISTRATION HELPERS
// =============================================================================

/**
 * Register a trail VFX effect and return the effect ID.
 *
 * Rebuilt on the trail grammar (trails-and-afterimages spec §5): the def is
 * now a `projectileRibbon` — a fading polyline the TrailSystem redraws from
 * the projectile's position history — instead of the old 3-copy scattered
 * circle one-shot that was never played in flight. All ~40 existing
 * `.withTrail(color, length)` call sites (plus every TOML projectile the
 * factory wires) upgrade through this one function with zero edits. The
 * legacy `length` knob maps to the ribbon head width; the second tone is
 * the auto white under-glow (spec Q5: keep the sugar).
 */
export function registerTrailVFX(abilityId: string, color: number, length?: number): string {
  const trailId = `${abilityId}_trail`;
  VFXEffectRegistry.register({
    id: trailId,
    name: `${abilityId} Trail`,
    category: 'trail',
    primitives: [projectileRibbon({ color, width: length ?? 5 })],
  });
  return trailId;
}

/**
 * Register an impact VFX effect and return the effect ID.
 */
export function registerImpactVFX(abilityId: string, type: 'explosion' | 'spark' | 'shatter' | 'freeze' | 'burn'): string {
  const impactId = `${abilityId}_impact_${type}`;
  VFXEffectRegistry.register({
    id: impactId,
    name: `${abilityId} Impact (${type})`,
    category: 'burst',
    primitives: IMPACT_VFX_CONFIGS[type] || IMPACT_VFX_CONFIGS.spark,
  });
  return impactId;
}

/**
 * Register a cast VFX effect and return the effect ID.
 */
export function registerCastVFX(abilityId: string, type: 'charge' | 'glow' | 'summon' | 'channel'): string {
  const castId = `${abilityId}_cast_${type}`;
  VFXEffectRegistry.register({
    id: castId,
    name: `${abilityId} Cast (${type})`,
    category: 'burst',
    primitives: CAST_VFX_CONFIGS[type] || CAST_VFX_CONFIGS.glow,
  });
  return castId;
}

/**
 * Register an aura VFX effect and return the effect ID.
 * followTarget defaults to 'player' (self-buffs); pass 'target' for debuff auras.
 */
export function registerAuraVFX(abilityId: string, color: number, radius?: number, pulse?: boolean, followTarget?: string): string {
  const auraId = `${abilityId}_aura`;
  const r = radius ?? 40;
  const prims: any[] = [
    { type: 'ellipse', width: r * 2, height: r * 2, color, alpha: 0.15, depth: 50, stroke: { color, width: 2 },
      tween: pulse ? { scale: 1.05, alpha: 0.25, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } : undefined },
  ];
  VFXEffectRegistry.register({ id: auraId, name: `${abilityId} Aura`, category: 'aura', followTarget: followTarget ?? 'player', primitives: prims });
  return auraId;
}
